import React, { useEffect, useState } from 'react';
import { 
  fetchSpotlights, 
  agreeToSpotlight, 
  fetchSpotlightComments,
  addSpotlightComment,
  likeSpotlight,
  dislikeSpotlight,
  getSpotlightLikeStatus,
  SpotlightData 
} from '../../utils/api';
import Spotlight, { SpotlightProps } from './Spotlight';

// Import LeftContent type from the Spotlight component
interface LeftContent {
  type: 'gradient' | 'image' | 'video' | 'fullScreenVideo' | 'confetti';
  content: string;
  alt?: string;
  showControls?: boolean;
  autoPlay?: boolean;
}

interface SpotlightManagerProps {
  /** Optional flag to disable spotlights */
  disabled?: boolean;
  /** Optional callback when all spotlights are dismissed */
  onAllDismissed?: () => void;
  /** Filter function to show only specific spotlights */
  filter?: (spotlight: SpotlightData) => boolean;
}

/**
 * SpotlightManager - Manages loading and displaying spotlights from the API
 * 
 * This component fetches spotlights from the API and displays them to the user
 */
const SpotlightManager: React.FC<SpotlightManagerProps> = ({
  disabled = false,
  onAllDismissed,
  filter
}) => {
  const [spotlight, setSpotlight] = useState<SpotlightData | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [likes, setLikes] = useState<number>(0);
  const [hasLiked, setHasLiked] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);

  // Load spotlight from API
  const loadSpotlight = async (preferredUuid?: string) => {
    if (disabled) return;

    try {
      setLoading(true);
      // console.log('Fetching spotlight from API...', preferredUuid ? `with preferred UUID: ${preferredUuid}` : '');
      const result = await fetchSpotlights(preferredUuid);
              // console.log('API response data:', result);
      
      // Check if we should filter this spotlight
      if (result && filter && !filter(result)) {
        console.log('Spotlight filtered out by filter function');
        setSpotlight(null);
        if (onAllDismissed) {
          onAllDismissed();
        }
        return;
      }
      
      setSpotlight(result);
      
      // If we have a spotlight, load its comments and open it
      if (result) {
        // console.log('Opening spotlight:', result);
        
        // Load comments for this spotlight
        loadComments(result.id);
        
        // Load like status for this spotlight
        loadLikeStatus(result.id);
        
        setIsSpotlightOpen(true);
      } else if (onAllDismissed) {
        console.log('No spotlight available, calling onAllDismissed');
        onAllDismissed();
      }
    } catch (err) {
      console.error('Failed to load spotlight:', err);
      setError('Failed to load spotlight. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadSpotlight();
  }, [disabled, filter, onAllDismissed]);

  // Listen for spotlight refresh events
  useEffect(() => {
    const handleSpotlightRefresh = (event: CustomEvent) => {
      console.log('🔦 Spotlight refresh event received in SpotlightManager:', event.detail);
      
      // Extract preferred spotlight UUID from the event
      const preferredSpotlightId = event.detail?.spotlight_id;
      
      if (preferredSpotlightId) {
        console.log('🔦 Reloading spotlights with preferred UUID:', preferredSpotlightId);
        loadSpotlight(preferredSpotlightId);
      } else {
        console.log('🔦 Reloading spotlights without preferred UUID');
        loadSpotlight();
      }
    };

    // Listen for spotlight refresh events
    window.addEventListener('spotlight_refresh', handleSpotlightRefresh as EventListener);
    
    return () => {
      window.removeEventListener('spotlight_refresh', handleSpotlightRefresh as EventListener);
    };
  }, []);

  // Function to load comments for a spotlight
  const loadComments = async (spotlightId: string) => {
    try {
      // console.log(`Loading comments for spotlight ${spotlightId}`);
      const commentsData = await fetchSpotlightComments(spotlightId);
      // console.log('Loaded comments:', commentsData);
      
      if (Array.isArray(commentsData)) {
        setComments(commentsData);
      } else if (commentsData.data && Array.isArray(commentsData.data)) {
        setComments(commentsData.data);
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };
  
  // Function to load like status for a spotlight
  const loadLikeStatus = async (spotlightId: string) => {
    try {
      // console.log(`Loading like status for spotlight ${spotlightId}`);
      const likeStatus = await getSpotlightLikeStatus(spotlightId);
      // console.log('Loaded like status:', likeStatus);
      
      setHasLiked(likeStatus.hasLiked);
      setLikes(likeStatus.likesCount);
    } catch (err) {
      console.error('Failed to load like status:', err);
      // Fall back to using the spotlight's social data if available
      if (spotlight && spotlight.social && typeof spotlight.social.likes === 'number') {
        setLikes(spotlight.social.likes);
      }
    }
  };
  
  // Function to handle likes
  const handleLike = async (liked: boolean) => {
    if (!spotlight) return;
    
    try {
      // console.log(`${liked ? 'Liking' : 'Disliking'} spotlight:`, spotlight.id);
      
      if (liked) {
        await likeSpotlight(spotlight.id);
      } else {
        await dislikeSpotlight(spotlight.id);
      }
      
      // Update local state to reflect the change immediately
      setHasLiked(liked);
      
      // Refresh the like status from the server
      await loadLikeStatus(spotlight.id);
    } catch (err) {
      console.error('Failed to like/dislike spotlight:', err);
      // Revert local state if the API call fails
      setHasLiked(!liked);
    }
  };
  
  // Function to add a new comment
  const handleAddComment = async (comment: { text: string, visibility: 'everyone' | 'sender', timestamp: string }) => {
    if (!spotlight) return;
    
    try {
      // console.log('Adding comment:', comment);
      const result = await addSpotlightComment(
        spotlight.id, 
        comment.text, 
        comment.visibility
      );
      
      if (result) {
        // console.log('Comment added successfully:', result);
        // Reload all comments to get the latest
        await loadComments(spotlight.id);
      }
    } catch (err) {
      console.error('Failed to add comment:', err);
    }
  };
  
  const handleSpotlightClose = () => {
    setIsSpotlightOpen(false);
    
    // If we're closing naturally, call onAllDismissed
    if (onAllDismissed) {
      onAllDismissed();
    }
  };
  
  const handleDismiss = async (data: any = {}) => {
    if (!spotlight) return;
    
    // console.log('Dismissing spotlight:', spotlight.id, data);
    
    try {
      await agreeToSpotlight(spotlight.id, data);
      // console.log('Spotlight dismissed successfully');
      
      // Clear the spotlight
      setSpotlight(null);
      handleSpotlightClose();
      
      // Call onAllDismissed since we've handled this spotlight
      if (onAllDismissed) {
        // console.log('Calling onAllDismissed after dismissing spotlight');
        onAllDismissed();
      }
    } catch (err) {
      console.error('Failed to dismiss spotlight:', err);
      // Still close the spotlight even if the API call fails
      handleSpotlightClose();
    }
  };
  
  // Don't render anything if disabled, loading, or no spotlight
  if (disabled || loading || !spotlight) {
    return null;
  }
  
  // Map the API spotlight data to the Spotlight component props
  const mapSpotlightToProps = (data: SpotlightData): SpotlightProps => {
    // Add null/undefined checks
    if (!data) {
      console.error('Spotlight is undefined');
      return {
        id: 'error-spotlight',
        isOpen: isSpotlightOpen,
        onClose: handleSpotlightClose,
        title: 'Error',
        dismissButton: {
          text: 'Close',
          onClick: handleDismiss
        }
      };
    }

    // console.log('Mapping spotlight to props:', data);
    
    // Ensure content exists with safe defaults
    const content = data.content || {
      title: 'No Title',
    };
    
    // console.log('Content object:', content);
    
    // Ensure social exists with safe defaults
    const social = data.social || {
      likes: 0,
      comments: [],
      allowComments: false,
      allowQuestions: false
    };
    
    // Map and validate leftContent
    let mappedLeftContent: LeftContent | undefined = undefined;
    
    if (content.leftContent) {
      const apiLeftContent = content.leftContent;
      
      // Validate type is one of the allowed types, default to 'gradient' if not
      const validType = ['gradient', 'image', 'video', 'fullScreenVideo', 'confetti'].includes(apiLeftContent.type)
        ? apiLeftContent.type as LeftContent['type']
        : 'gradient';
      
      mappedLeftContent = {
        type: validType,
        content: apiLeftContent.content || '',
        alt: apiLeftContent.alt
      };
    }
    
    // Add default gradient if no leftContent provided
    if (!mappedLeftContent) {
      mappedLeftContent = {
        type: 'gradient',
        content: 'linear-gradient(to right bottom, #4f46e5, #7c3aed)',
        alt: 'Default background'
      };
    }
    
    // Get title from content
    const title = content.title || '';
    // console.log('Final title value:', title);
    
    // Map survey data from API to the format expected by Spotlight component
    let surveyData = null;
    if (content.survey) {
      if (content.survey.questions && content.survey.questions.length > 0) {
        // Use the questions array directly if available
        surveyData = {
          questions: content.survey.questions
        };
      } else if (content.survey.question && typeof content.survey.question === 'string') {
        // Use the legacy format with single question and options
        surveyData = {
          question: content.survey.question,
          options: Array.isArray(content.survey.options) ? content.survey.options : []
        };
      }
    }
    
    // Map agreement data
    let agreementData = null;
    if (content.agreement && content.agreement.text) {
      agreementData = {
        text: content.agreement.text,
        required: !!content.agreement.required
      };
    }
    
    // Map external link
    let externalLinkData = null;
    if (content.externalLink && content.externalLink.url && content.externalLink.text) {
      externalLinkData = {
        url: content.externalLink.url,
        text: content.externalLink.text
      };
    }
    
    // Map form data
    let formData = null;
    if (content.form && content.form.fields && Array.isArray(content.form.fields)) {
      formData = content.form;
    }
    
    const finalProps = {
      id: data.id || `spotlight-${Date.now()}`,
      isOpen: isSpotlightOpen,
      onClose: handleSpotlightClose,
      title,
      subtitle: content.subtitle || '',
      description: content.description || '',
      leftContent: mappedLeftContent,
      survey: surveyData,
      form: formData,
      externalLink: externalLinkData,
      agreement: agreementData,
      sender: data.sender || null,
      required: !!data.required,
      // Social interactions
      showSocialInteractions: !!social,
      likes: likes,
      hasLiked: hasLiked,
      onLike: handleLike,
      comments: comments || [],
      onAddComment: handleAddComment,
      allowComments: !!social.allowComments,
      allowQuestions: !!social.allowQuestions,
      // Deadline
      deadline: data.deadline || null,
      // Dismiss button
      dismissButton: {
        text: 'Ik heb dit gelezen',
        onClick: handleDismiss
      }
    };
    
    return finalProps;
  };
  
  return (
    <Spotlight 
      {...mapSpotlightToProps(spotlight)}
    />
  );
};

export default SpotlightManager; 